import { useState } from 'react'
import { formatTimeRange, readableDescription, toMapLink } from '../lib/searchUtils.js'
import { getEventPinConfig } from '../lib/pinConfig.js'
import { getMapApiKey } from '../lib/mapConfig.js'
import ImageCarousel from './ImageCarousel.jsx'
import SaveButton from './SaveButton.jsx'

export default function EventDetailFull({ event, isSaved, onToggleSave }) {
    const [copySuccess, setCopySuccess] = useState(false)

    if (!event) {
        return null
    }

    const handleShare = async () => {
        const shareLink = `${window.location.origin}/event/${event.id}`
        try {
            await navigator.clipboard.writeText(shareLink)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        } catch (err) {
            console.error('Failed to copy link:', err)
        }
    }

    // Generate embedded Google Maps URL
    const getEmbedMapUrl = (event) => {
        if (!event?.lat_y || !event?.lon_x) {
            return null
        }

        const lat = event.lat_y
        const lng = event.lon_x
        const apiKey = getMapApiKey()

        const params = new URLSearchParams()
        params.set('q', event.name ? `${event.name} @ ${lat},${lng}` : `${lat},${lng}`)
        if (apiKey) {
            params.set('key', apiKey)
        }
        return `https://www.google.com/maps/embed/v1/place?${params.toString()}`
    }

    // Extract event data
    const categoryLabel = event?.category ?? 'Event'
    const pinConfig = getEventPinConfig(event?.category ?? event?.ml_category)
    const description = readableDescription(event?.description)
    const timeRange = formatTimeRange(event?.time_start, event?.time_end)
    const mapsLink = toMapLink(event)
    const images = [...new Set(event?.images?.filter(Boolean) ?? [])]
    const urls = [...new Set(event?.urls?.filter(Boolean) ?? [])]
    const reservationUrls = [...new Set(event?.reservation_url?.filter(Boolean) ?? [])]
    const socialLinks = [...new Set(event?.social_link?.filter(Boolean) ?? [])]
    const phones = [...new Set(event?.telephone?.filter(Boolean) ?? [])]
    const emails = [...new Set(event?.email?.filter(Boolean) ?? [])]

    const linkItems = []
    urls.forEach((url, index) => {
        linkItems.push({ href: url, label: urls.length > 1 ? `Website ${index + 1}` : 'Website' })
    })
    reservationUrls.forEach((url, index) => {
        if (!linkItems.some(link => link.href === url)) {
            linkItems.push({ href: url, label: reservationUrls.length > 1 ? `Get Tickets ${index + 1}` : 'Get Tickets' })
        }
    })
    socialLinks.forEach((url, index) => {
        if (!linkItems.some(link => link.href === url)) {
            linkItems.push({ href: url, label: socialLinks.length > 1 ? `Social ${index + 1}` : 'Social' })
        }
    })

    const embedMapUrl = getEmbedMapUrl(event)

    return (
        <div className="mx-auto w-full max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-2">
                {/* Left column - Event details */}
                <div className="space-y-6">
                    <div>
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-700">
                            {categoryLabel}
                        </span>
                        <h1 className="mt-4 text-4xl font-bold text-slate-900">
                            {pinConfig?.glyph && <span className="mr-2">{pinConfig.glyph}</span>}
                            {event?.name ?? 'Untitled Event'}
                        </h1>
                    </div>

                    {timeRange && (
                        <div className="rounded-xl border border-purple-200 bg-purple-50 p-6 shadow-sm">
                            <h2 className="mb-2 text-lg font-semibold text-slate-900">When</h2>
                            <p className="text-lg font-medium text-purple-900">{timeRange}</p>
                        </div>
                    )}

                    {images.length > 0 && (
                        <div className="space-y-4">
                            <h2 className="text-lg font-semibold text-slate-900">Images</h2>
                            {images.map((image, index) => (
                                <div key={index} className="overflow-hidden rounded-2xl shadow-md">
                                    <ImageCarousel
                                        images={[image]}
                                        alt={event?.name ? `${event.name} image ${index + 1}` : `Event image ${index + 1}`}
                                        className="h-96 w-full object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {description && (
                        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="mb-3 text-lg font-semibold text-slate-900">About This Event</h2>
                            <p className="leading-relaxed text-slate-700">{description}</p>
                        </div>
                    )}

                    {(event?.place_name || event?.address) && (
                        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="mb-3 text-lg font-semibold text-slate-900">Location</h2>
                            {event?.place_name && (
                                <p className="font-medium text-slate-900">{event.place_name}</p>
                            )}
                            {event?.address && (
                                <p className="text-slate-700">{event.address}</p>
                            )}
                        </div>
                    )}

                    {(phones.length > 0 || emails.length > 0) && (
                        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="mb-3 text-lg font-semibold text-slate-900">Contact</h2>
                            <div className="space-y-3">
                                {phones.length > 0 && (
                                    <div className="space-y-2">
                                        {phones.map((phone, index) => {
                                            const telHref = `tel:${phone.replace(/[^0-9+]/g, '')}`
                                            return (
                                                <div key={index} className="flex items-center gap-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5 text-slate-400">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                                    </svg>
                                                    <a
                                                        href={telHref}
                                                        className="text-logo-pink transition hover:text-purple-600"
                                                    >
                                                        {phone}
                                                    </a>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                                {emails.length > 0 && (
                                    <div className="space-y-2">
                                        {emails.map((email, index) => {
                                            const emailHref = `mailto:${email}`
                                            return (
                                                <div key={index} className="flex items-center gap-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5 text-slate-400">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                                    </svg>
                                                    <a
                                                        href={emailHref}
                                                        className="text-logo-pink transition hover:text-purple-600"
                                                    >
                                                        {email}
                                                    </a>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="mb-4 text-lg font-semibold text-slate-900">Links</h2>
                        <div className="flex flex-wrap gap-3">
                            {linkItems.map(link => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-logo-pink transition hover:bg-purple-100 hover:text-purple-600"
                                >
                                    {link.label}
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                    </svg>
                                </a>
                            ))}
                            {mapsLink && (
                                <a
                                    href={mapsLink}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-logo-pink transition hover:bg-purple-100 hover:text-purple-600"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                    </svg>
                                    Open in Google Maps
                                </a>
                            )}
                            {isSaved !== undefined && onToggleSave && (
                                <SaveButton
                                    item={event}
                                    isSaved={isSaved}
                                    onToggle={onToggleSave}
                                    variant="default"
                                />
                            )}
                            <button
                                type="button"
                                onClick={handleShare}
                                className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-logo-pink transition hover:bg-purple-100 hover:text-purple-600"
                                title="Copy shareable link"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                                </svg>
                                {copySuccess ? 'Copied!' : 'Share'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right column - Map */}
                <div className="lg:sticky lg:top-6 lg:h-fit">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <h2 className="mb-4 text-lg font-semibold text-slate-900">Event Location</h2>
                        {embedMapUrl ? (
                            <div className="overflow-hidden rounded-xl">
                                <iframe
                                    src={embedMapUrl}
                                    width="100%"
                                    height="400"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title={`Map showing ${event?.name || 'event location'}`}
                                />
                            </div>
                        ) : (
                            <div className="flex h-64 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="mx-auto h-12 w-12">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                    </svg>
                                    <p className="mt-2">Location not available</p>
                                </div>
                            </div>
                        )}
                        {event?.lat_y && event?.lon_x && (
                            <div className="mt-4 text-sm text-slate-600">
                                <p>Coordinates: {event.lat_y.toFixed(6)}, {event.lon_x.toFixed(6)}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
